import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Globe, LogOut, Menu, ShieldCheck, ShoppingBag, UserRound, X } from "lucide-react";
import { useI18n } from "@/i18n";
import { useAuth } from "@/lib/auth";
import BrandMark from "./BrandMark";
import { useCart } from "@/lib/cart";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [localeOpen, setLocaleOpen] = useState(false);
  const { t, locale, setLocale } = useI18n();
  const { user, checkAuth, initialized, logout } = useAuth();
  const location = useLocation();
  const { itemCount, openCart } = useCart();

  useEffect(() => {
    if (!initialized) checkAuth();
  }, [initialized, checkAuth]);

  useEffect(() => {
    setIsOpen(false);
    setAccountOpen(false);
    setLocaleOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!accountOpen && !localeOpen) return;
    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".account-menu")) setAccountOpen(false);
      if (!target.closest(".minimal-locale-menu")) setLocaleOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [accountOpen, localeOpen]);

  function selectLocale(value: "it" | "en") {
    setLocale(value);
    setLocaleOpen(false);
  }

  const initials = user
    ? (user.name || user.email)
        .split(/\s+/)
        .map((part) => part[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "";

  async function handleLogout() {
    setAccountOpen(false);
    setIsOpen(false);
    await logout();
  }

  const navLinks = [
    { href: "/", label: t.nav.home },
    { href: "/gallery", label: t.nav.shopAll },
    { href: "/gallery/print", label: t.nav.print },
    { href: "/gallery/original", label: t.nav.original },
    { href: "/about", label: t.nav.about },
    { href: "/contact", label: t.nav.contact },
  ];

  const localeOptions = [
    { value: "it" as const, label: "IT" },
    { value: "en" as const, label: "EN" },
  ];

  return (
    <header className="minimal-header">
      <div className="minimal-nav container">
        <button
          onClick={() => setIsOpen((value) => !value)}
          className="minimal-icon-button mobile-menu-button"
          aria-label="Toggle menu"
        >
          {isOpen ? <X size={22} /> : <Menu size={22} />}
        </button>

        <nav className="minimal-desktop-links" aria-label="Main navigation">
          {navLinks.slice(0, 5).map((link) => (
            <Link key={link.href} to={link.href} className={location.pathname === link.href ? "active" : ""}>
              {link.label}
            </Link>
          ))}
        </nav>

        <BrandMark />

        <div className="minimal-nav-actions">
          <Link to="/contact" className="minimal-contact-link">{t.nav.contact}</Link>
          <div className="minimal-locale-menu">
            <button
              type="button"
              onClick={() => setLocaleOpen((value) => !value)}
              className="minimal-icon-button"
              aria-label="Language selector"
              aria-haspopup="menu"
              aria-expanded={localeOpen}
            >
              <Globe size={19} />
            </button>
            {localeOpen && (
              <div className="locale-dropdown" role="menu" aria-label="Language selector">
                {localeOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => selectLocale(option.value)}
                    className={locale === option.value ? "active" : ""}
                    role="menuitemradio"
                    aria-checked={locale === option.value}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {user ? (
            <div className="account-menu">
              <button
                onClick={() => setAccountOpen((value) => !value)}
                className="minimal-icon-button account-action"
                aria-label={t.nav.account}
                aria-haspopup="menu"
                aria-expanded={accountOpen}
              >
                <span className="account-initial">{initials || "•"}</span>
              </button>
              {accountOpen && (
                <div className="account-dropdown" role="menu">
                  <Link to="/account" onClick={() => setAccountOpen(false)}>{t.nav.account}</Link>
                  {user.role === "admin" && <Link to="/admin" onClick={() => setAccountOpen(false)}>{t.nav.admin}</Link>}
                  <button onClick={() => void handleLogout()}>{t.nav.logout}</button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" className="minimal-icon-button account-action" aria-label={t.nav.login}>
              <UserRound size={19} />
            </Link>
          )}
          <button onClick={openCart} className="minimal-icon-button cart-nav-button" aria-label={t.nav.cart}>
            <ShoppingBag size={19} />
            {itemCount > 0 && <span className="cart-count">{itemCount}</span>}
          </button>
        </div>
      </div>

      {isOpen && (
        <nav className="minimal-mobile-menu">
          {navLinks.map((link) => <Link key={link.href} to={link.href}>{link.label}</Link>)}
          <div className="mobile-menu-utilities">
            <div className="mobile-locale-picker">
              <span><Globe size={16} />Language</span>
              <div className="mobile-locale-options">
                {localeOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => selectLocale(option.value)}
                    className={locale === option.value ? "active" : ""}
                    aria-pressed={locale === option.value}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mobile-menu-account">
              {user ? (
                <>
                  <Link to="/account" onClick={() => setIsOpen(false)}><UserRound size={16} />{t.nav.account}</Link>
                  {user.role === "admin" && <Link to="/admin" onClick={() => setIsOpen(false)}><ShieldCheck size={16} />{t.nav.admin}</Link>}
                  <button onClick={() => void handleLogout()}><LogOut size={16} />{t.nav.logout}</button>
                </>
              ) : (
                <Link to="/login" onClick={() => setIsOpen(false)}><UserRound size={16} />{t.nav.login}</Link>
              )}
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}
